/**
 * useFiscalPeriodCheck
 *
 * Checks the fiscal period for a selected transaction date and returns a warning
 * when that date falls in a CLOSED or LOCKED period. Wired into transaction forms
 * so the user gets immediate feedback on date selection — mirroring the server's
 * validatePeriodOpen rule (closed → block, locked → block, no period → allow).
 */

import { useEffect, useState } from 'react'

import { getPeriodForDate } from '../services/fiscal-period-service'

export interface FiscalPeriodCheckResult {
  /** Non-null when the selected date is in a closed/locked period; ready to display. */
  warning: string | null
  isChecking: boolean
}

/** Normalize a Date or ISO-ish string to a local YYYY-MM-DD, or null if unusable. */
function toDateString(date: Date | string | null | undefined): string | null {
  if (!date) { return null }
  if (typeof date === 'string') { return date.length >= 10 ? date.slice(0, 10) : null }
  if (Number.isNaN(date.getTime())) { return null }
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function useFiscalPeriodCheck(
  date: Date | string | null | undefined,
  orgId: string | null | undefined,
): FiscalPeriodCheckResult {
  const [warning, setWarning] = useState<string | null>(null)
  const [isChecking, setIsChecking] = useState(false)
  const dateStr = toDateString(date)

  useEffect(() => {
    if (!dateStr || !orgId) { setWarning(null); return }

    let isCancelled = false
    setIsChecking(true)
    void (async () => {
      try {
        const res = await getPeriodForDate(orgId, dateStr)
        if (isCancelled) { return }
        const period = res.data
        // No period covers the date → allow (the server permits this too).
        if (!period) { setWarning(null); return }
        if (period.status === 'PERIOD_STATUS_CLOSED') {
          setWarning(`The selected date falls in "${period.name}", which is closed. Reopen the period before posting here.`)
        } else if (period.status === 'PERIOD_STATUS_LOCKED') {
          setWarning(`The selected date falls in "${period.name}", which is locked — no transactions allowed in locked periods.`)
        } else {
          setWarning(null)
        }
      } catch {
        // Network/permission error: don't block the user — the server validates on submit.
        if (!isCancelled) { setWarning(null) }
      } finally {
        if (!isCancelled) { setIsChecking(false) }
      }
    })()

    return () => { isCancelled = true }
  }, [dateStr, orgId])

  return { warning, isChecking }
}
